import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Input,
  Button,
  InputGroup,
  InputLeftElement,
  Card,
  CardBody,
  Icon,
  useToast,
  useColorModeValue,
  Flex,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  useDisclosure,
  Checkbox,
  Tooltip,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  IconButton,
  Skeleton,
  Stack,
  Spinner,
  keyframes,
} from '@chakra-ui/react'
import { useState, useEffect, useCallback, useMemo, ReactNode } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  LuSearch,
  LuBookmark,
  LuBookOpen,
} from 'react-icons/lu'
import { useTranslation } from 'react-i18next'
import axiosClient from '../../api/axios'
import { ArticleCard } from './components/ArticleCard'
import { PaginationCarousel } from './components/PaginationCarousel'
import { ProjectsListView } from './components/ProjectsListView'
import { ProjectWorkspace } from './components/ProjectWorkspace'
import { CreateProjectModal } from './components/CreateProjectModal'
import { Article, SearchFilters, SearchOptions } from './types'

const normalizeText = (value?: string) =>
  value?.toLowerCase().replace(/\s+/g, ' ').trim() || ''

const createArticleKey = (article: Article, fallback?: string) => {
  const parts = [
    article.pubmedId?.trim(),
    article.doi?.trim(),
    article.url?.trim(),
    normalizeText(article.title),
    normalizeText(article.journal),
    article.publicationDate?.trim(),
  ].filter(Boolean)

  if (parts.length === 0) {
    const abstractKey = normalizeText(article.abstract)
    if (abstractKey) return abstractKey
    return fallback
  }

  return parts.join('|')
}

const deduplicateArticles = (articles: Article[] = []) => {
  const seen = new Set<string>()

  return articles.filter((article, index) => {
    const key =
      createArticleKey(article, `${normalizeText(article.title)}-${index}`) ||
      JSON.stringify(article)

    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const STOP_WORDS = new Set([
  'a',
  'an',
  'and',
  'are',
  'as',
  'at',
  'be',
  'but',
  'by',
  'for',
  'if',
  'in',
  'into',
  'is',
  'it',
  'no',
  'not',
  'of',
  'on',
  'or',
  'such',
  'that',
  'the',
  'their',
  'then',
  'there',
  'these',
  'they',
  'this',
  'to',
  'was',
  'will',
  'with',
])


const buildHighlightRegex = (query: string) => {
  const terms =
    query
      ?.split(/[\s,]+/)
      .map((term) => term.trim())
      .filter((term) => term && !STOP_WORDS.has(term.toLowerCase()))
      .map((term) => escapeRegExp(term)) ?? []

  if (!terms.length) return null

  return new RegExp(`(${terms.join('|')})`, 'gi')
}

const highlightMatches = (text: string, regex: RegExp | null): ReactNode => {
  if (!regex || !text) return text
  const parts = text.split(regex)
  if (parts.length === 1) return text

  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <Text
        as="span"
        key={`highlight-${part}-${index}`}
        bg="yellow.200"
        color="black"
        px={1}
        borderRadius="sm"
        fontWeight="semibold"
      >
        {part}
      </Text>
    ) : (
      <span key={`text-${part}-${index}`}>{part}</span>
    )
  )
}

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`

export default function MedInsight() {
  const { t } = useTranslation()
  const { projectId } = useParams<{ projectId?: string }>()
  const navigate = useNavigate()
  const toast = useToast()
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState<Article[]>([])
  const [bookmarkedArticles, setBookmarkedArticles] = useState<Article[]>([])
  const [isLoadingBookmarks, setIsLoadingBookmarks] = useState(false)
  const [filters, setFilters] = useState<SearchFilters>({})
  const [resultsPerPage, setResultsPerPage] = useState(20)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalResultsCount, setTotalResultsCount] = useState(0)
  const [includeBroaderSources, setIncludeBroaderSources] = useState(false)
  const {
    isOpen: isProjectModalOpen,
    onOpen: openProjectModal,
    onClose: closeProjectModal,
  } = useDisclosure()
  const {
    isOpen: isSearchDrawerOpen,
    onOpen: openSearchDrawer,
    onClose: closeSearchDrawer,
  } = useDisclosure()
  const {
    isOpen: isBookmarksDrawerOpen,
    onOpen: openBookmarksDrawer,
    onClose: closeBookmarksDrawer,
  } = useDisclosure()

  const [researchProjects, setResearchProjects] = useState<any[]>([])
  const [selectedArticleForProject, setSelectedArticleForProject] = useState<Article | null>(null)
  const [selectedProjectId, setSelectedProjectId] = useState('')
  const [isLoadingProjects, setIsLoadingProjects] = useState(false)
  const [isAddingToProject, setIsAddingToProject] = useState(false)
  const [selectedProject, setSelectedProject] = useState<any | null>(null)
  const {
    isOpen: isCreateProjectModalOpen,
    onOpen: openCreateProjectModal,
    onClose: closeCreateProjectModal,
  } = useDisclosure()

  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const hoverBg = useColorModeValue('gray.50', 'gray.700')
  const pageBg = useColorModeValue('gray.50', 'gray.900')
  const totalPages =
    totalResultsCount > 0 ? Math.max(1, Math.ceil(totalResultsCount / resultsPerPage)) : 0
  const rangeStart =
    totalResultsCount > 0 ? (currentPage - 1) * resultsPerPage + 1 : 0
  const rangeEnd =
    totalResultsCount > 0
      ? Math.min(totalResultsCount, rangeStart + Math.max(searchResults.length - 1, 0))
      : 0
  const highlightRegex = useMemo(() => buildHighlightRegex(searchQuery), [searchQuery])
  const highlightContent = useCallback(
    (text: string) => highlightMatches(text, highlightRegex),
    [highlightRegex]
  )

  const handleSearch = useCallback(
    async ({
      page = currentPage,
      perPage = resultsPerPage,
      queryOverride,
    }: SearchOptions = {}) => {
      const queryToUse = queryOverride ?? searchQuery
      if (!queryToUse.trim()) {
        toast({
          title: 'Please enter a search query',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        })
        return
      }

      setIsSearching(true)
      try {
        const response = await axiosClient.post('literature/search', {
          query: queryToUse,
          maxResults: perPage,
          page,
          filters,
          includeBroaderSources,
        })

        const uniqueResults = deduplicateArticles(response.data.results || [])

        setSearchResults(uniqueResults)
        setTotalResultsCount(Number(response.data.count) || 0)
        setCurrentPage(response.data.page || page)
        setResultsPerPage(perPage)

        let description = `Showing ${uniqueResults.length} results on page ${response.data.page || page}`;
        if (response.data.sources?.length > 1) {
          description += ` (from ${response.data.sources.join(', ')})`;
        }

        toast({
          title: `Found ${response.data.count} articles`,
          description,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })

        // Open search drawer to show results
        openSearchDrawer()
      } catch (error: any) {
        toast({
          title: 'Search failed',
          description: error.response?.data?.message || 'Unable to search articles',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      } finally {
        setIsSearching(false)
      }
    },
    [searchQuery, filters, toast, currentPage, resultsPerPage, includeBroaderSources, openSearchDrawer]
  )

  const handleResultsPerPageChange = useCallback(
    (value: string) => {
      const parsedValue = parseInt(value, 10) || 10
      setResultsPerPage(parsedValue)
      setCurrentPage(1)

      if (searchQuery.trim()) {
        handleSearch({ page: 1, perPage: parsedValue })
      }
    },
    [handleSearch, searchQuery]
  )

  const handlePageChange = useCallback(
    (page: number) => {
      if (page === currentPage || page < 1 || (totalPages && page > totalPages)) {
        return
      }

      handleSearch({ page, perPage: resultsPerPage })
    },
    [handleSearch, currentPage, totalPages, resultsPerPage]
  )

  const loadBookmarks = useCallback(async () => {
    setIsLoadingBookmarks(true)
    try {
      const response = await axiosClient.get('literature/bookmarks')
      setBookmarkedArticles(response.data.bookmarks || [])
    } catch (error: any) {
      console.error('Failed to load bookmarks:', error)
    } finally {
      setIsLoadingBookmarks(false)
    }
  }, [])

  const handleBookmark = useCallback(
    async (article: Article) => {
      try {
        await axiosClient.post('literature/bookmark', { article })

        toast({
          title: 'Article bookmarked',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })

        loadBookmarks()
      } catch (error: any) {
        if (error.response?.status === 409) {
          toast({
            title: 'Already bookmarked',
            description: 'This article is already in your bookmarks',
            status: 'info',
            duration: 3000,
            isClosable: true,
          })
        } else {
          toast({
            title: 'Bookmark failed',
            description: error.response?.data?.message || 'Unable to bookmark article',
            status: 'error',
            duration: 5000,
            isClosable: true,
          })
        }
      }
    },
    [toast, loadBookmarks]
  )

  const fetchProjects = useCallback(async () => {
    setIsLoadingProjects(true)
    try {
      const response = await axiosClient.get('medinsight/projects')
      setResearchProjects(response.data.projects || [])
    } catch (error) {
      console.error('Failed to load projects:', error)
      toast({
        title: 'Failed to load projects',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsLoadingProjects(false)
    }
  }, [toast])

  const handleAddToProject = useCallback(
    async (article: Article) => {
      setSelectedArticleForProject(article)
      await fetchProjects()
      openProjectModal()
    },
    [fetchProjects, openProjectModal]
  )

  const handleConfirmAddToProject = useCallback(async () => {
    if (!selectedProjectId || !selectedArticleForProject) {
      return
    }

    setIsAddingToProject(true)
    try {
      await axiosClient.post(`medinsight/projects/${selectedProjectId}/articles`, {
        article: {
          title: selectedArticleForProject.title,
          abstract: selectedArticleForProject.abstract,
          authors: selectedArticleForProject.authors,
          journal: selectedArticleForProject.journal,
          publicationDate: selectedArticleForProject.publicationDate,
          doi: selectedArticleForProject.doi,
          pubmedId: selectedArticleForProject.pubmedId,
          pmid: selectedArticleForProject.pubmedId,
          externalId: selectedArticleForProject.pubmedId,
          url: selectedArticleForProject.url,
          keywords: selectedArticleForProject.keywords,
        },
      })

      toast({
        title: 'Article ajouté au projet',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })

      closeProjectModal()
      setSelectedArticleForProject(null)
      setSelectedProjectId('')
    } catch (error: any) {
      if (error.response?.status === 409) {
        toast({
          title: 'Article already in project',
          description: 'This article is already in the selected project',
          status: 'info',
          duration: 3000,
          isClosable: true,
        })
      } else {
        toast({
          title: 'Failed to add article',
          description: error.response?.data?.message || 'Unable to add article to project',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      }
    } finally {
      setIsAddingToProject(false)
    }
  }, [selectedProjectId, selectedArticleForProject, toast, closeProjectModal])

  // Load projects on mount
  useEffect(() => {
    fetchProjects()
  }, [fetchProjects])

  // Load project from URL if projectId is provided
  useEffect(() => {
    if (!projectId) return

    // Avoid reloading if this project is already loaded
    if (selectedProject && selectedProject.id === projectId) {
      console.log('⏭️ Project already loaded, skipping:', projectId)
      return
    }

    const loadProjectFromUrl = async () => {
      try {
        console.log('🔗 Loading project from URL:', projectId)
        const response = await axiosClient.get(`medinsight/projects/${projectId}`)

        if (response.data.success && response.data.project) {
          console.log('✅ Project loaded successfully:', response.data.project.title)
          setSelectedProject(response.data.project)
        } else {
          throw new Error('Invalid response format')
        }
      } catch (error) {
        console.error('❌ Error loading project from URL:', error)
        toast({
          title: 'Erreur',
          description: 'Impossible de charger le projet',
          status: 'error',
          duration: 5000,
        })
        // Navigate back to literature home on error
        navigate('/literature', { replace: true })
      }
    }

    loadProjectFromUrl()
  }, [projectId, selectedProject, toast, navigate])

  if (selectedProject) {
    return (
      <Box minH="100vh" bg={pageBg}>
        <ProjectWorkspace
          project={selectedProject}
          onBack={() => {
            setSelectedProject(null)
            fetchProjects()
            // Clear project ID from URL
            navigate('/literature', { replace: true })
          }}
        />
      </Box>
    )
  }

  return (
    <Box
      minH="100vh"
      bg={pageBg}
      position="relative"
      overflow="hidden"
    >
      {/* Decorative Background */}
      <Box
        position="absolute"
        top="-200px"
        right="-200px"
        width="600px"
        height="600px"
        borderRadius="full"
        bgGradient="linear(135deg, purple.200, pink.200)"
        opacity={0.2}
        filter="blur(120px)"
        pointerEvents="none"
        zIndex={0}
        animation={`${float} 8s ease-in-out infinite`}
      />
      <Box
        position="absolute"
        bottom="-150px"
        left="-150px"
        width="500px"
        height="500px"
        borderRadius="full"
        bgGradient="linear(135deg, blue.200, teal.200)"
        opacity={0.15}
        filter="blur(100px)"
        pointerEvents="none"
        zIndex={0}
        animation={`${float} 10s ease-in-out infinite`}
        style={{ animationDelay: '2s' }}
      />

      {/* Main Content */}
      <Box
        position="relative"
        zIndex={1}
        p={{
          base: '24px 16px',
          lg: '32px 60px',
          xl: '40px 100px',
          '2xl': '48px 150px',
        }}
      >
        {/* Header Section with Gradient */}
        <VStack spacing={8} align="stretch" mb={10} position="relative" zIndex={1}>
          <Box
            bgGradient="linear(135deg, brand.purple 0%, purple.500 50%, pink.400 100%)"
            borderRadius="2xl"
            p={{ base: 6, md: 8 }}
            position="relative"
            overflow="hidden"
            boxShadow="xl"
            _before={{
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)',
              pointerEvents: 'none',
            }}
          >
            <VStack spacing={6} align="stretch" position="relative" zIndex={1}>
              {/* Top bar avec titre et favoris */}
              <HStack justify="space-between" w="full">
                <HStack spacing={3}>
                  <Box
                    p={2}
                    bg="whiteAlpha.300"
                    backdropFilter="blur(10px)"
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="whiteAlpha.400"
                  >
                    <Icon as={LuBookOpen} boxSize={6} color="white" />
                  </Box>
                  <VStack spacing={0} align="start">
                    <Heading as="h1" size="md" color="white" fontWeight="bold" letterSpacing="tight">
                      {t('modules.medinsight')}
                    </Heading>
                    <Text fontSize="xs" color="whiteAlpha.900" fontWeight="medium">
                      {t('medinsight.workspaceSubtitle')}
                    </Text>
                  </VStack>
                </HStack>

                <Tooltip label="Mes favoris">
                  <IconButton
                    aria-label="Favoris"
                    icon={<LuBookmark />}
                    onClick={() => {
                      loadBookmarks()
                      openBookmarksDrawer()
                    }}
                    size="md"
                    variant="ghost"
                    colorScheme="whiteAlpha"
                    color="white"
                    borderRadius="lg"
                    _hover={{
                      bg: 'whiteAlpha.200',
                    }}
                  />
                </Tooltip>
              </HStack>

              {/* Titre de recherche centré */}
              <VStack spacing={3} align="center" w="full">
                <Heading
                  fontSize={{ base: 'xl', md: '2xl' }}
                  color="white"
                  fontWeight="semibold"
                  textAlign="center"
                >
                  {t('medinsight.searchTitle')}
                </Heading>
                <Text
                  fontSize="sm"
                  color="whiteAlpha.900"
                  textAlign="center"
                >
                  {t('medinsight.searchSubtitle')}
                </Text>
              </VStack>

              {/* Barre de recherche centrée */}
              <VStack spacing={3} align="center" w="full">
                <InputGroup maxW="700px" size="lg" w="full">
                  <InputLeftElement pointerEvents="none" h="full">
                    <Icon as={LuSearch} color="purple.400" boxSize={5} />
                  </InputLeftElement>
                  <Input
                    placeholder={t('medinsight.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    bg="white"
                    borderRadius="full"
                    border="none"
                    fontSize="md"
                    h="54px"
                    pl={12}
                    pr={4}
                    boxShadow="lg"
                    _focus={{
                      boxShadow: '0 0 0 4px rgba(167, 139, 250, 0.3)',
                      transform: 'translateY(-1px)',
                    }}
                    _placeholder={{
                      color: 'gray.400',
                    }}
                    transition="all 0.2s"
                  />
                </InputGroup>

                {/* Actions de recherche */}
                <HStack spacing={4} justify="center" flexWrap="wrap">
                  <Button
                    onClick={() => handleSearch()}
                    isLoading={isSearching}
                    bg="white"
                    color="purple.600"
                    size="md"
                    borderRadius="full"
                    px={8}
                    fontWeight="semibold"
                    boxShadow="md"
                    _hover={{
                      bg: 'whiteAlpha.900',
                      transform: 'translateY(-1px)',
                      boxShadow: 'lg',
                    }}
                    transition="all 0.2s"
                  >
                    {t('medinsight.searchButton')}
                  </Button>
                  <Checkbox
                    isChecked={includeBroaderSources}
                    onChange={(e) => setIncludeBroaderSources(e.target.checked)}
                    colorScheme="whiteAlpha"
                    color="white"
                    size="sm"
                  >
                    {t('medinsight.includeBroaderSources')}
                  </Checkbox>
                </HStack>
              </VStack>
            </VStack>
          </Box>
        </VStack>

        {/* Projects Section */}
        <ProjectsListView
          projects={researchProjects}
          isLoading={isLoadingProjects}
          onProjectClick={(project) => {
            setSelectedProject(project)
            // Update URL with project ID
            navigate(`/literature/${project.id}`, { replace: true })
          }}
          onOpenWorkspace={(project) => {
            setSelectedProject(project)
            // Update URL with project ID
            navigate(`/literature/${project.id}`, { replace: true })
          }}
          onCreateProject={openCreateProjectModal}
          cardBg={cardBg}
          borderColor={borderColor}
        />
      </Box>

      {/* Search Drawer */}
      <Drawer isOpen={isSearchDrawerOpen} placement="right" onClose={closeSearchDrawer} size="xl">
        <DrawerOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader
            bgGradient="linear(135deg, brand.purple 0%, purple.500 100%)"
            color="white"
          >
            <HStack>
              <Icon as={LuSearch} />
              <Text>Rechercher des articles</Text>
            </HStack>
          </DrawerHeader>

          <DrawerBody px={0}>
            <VStack spacing={4} align="stretch" px={6} pt={4}>
              {/* Search Input */}
              <InputGroup size="lg">
                <InputLeftElement>
                  <Icon as={LuSearch} color="purple.500" />
                </InputLeftElement>
                <Input
                  placeholder="Rechercher dans PubMed..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch({ page: 1 })}
                  borderRadius="xl"
                />
              </InputGroup>

              {/* Filters */}
              <Card variant="outline" borderRadius="xl">
                <CardBody>
                  <VStack spacing={3} align="stretch">
                    <HStack spacing={2}>
                      <Input
                        type="date"
                        size="sm"
                        borderRadius="lg"
                        onChange={(e) =>
                          setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))
                        }
                      />
                      <Text fontSize="sm" color="gray.500">
                        to
                      </Text>
                      <Input
                        type="date"
                        size="sm"
                        borderRadius="lg"
                        onChange={(e) =>
                          setFilters((prev) => ({ ...prev, dateTo: e.target.value }))
                        }
                      />
                    </HStack>

                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600">
                        Résultats par page
                      </Text>
                      <Select
                        size="sm"
                        value={resultsPerPage}
                        onChange={(e) => handleResultsPerPageChange(e.target.value)}
                        w="100px"
                        borderRadius="lg"
                      >
                        {[10, 20, 30, 40, 50].map((value) => (
                          <option key={value} value={value}>
                            {value}
                          </option>
                        ))}
                      </Select>
                    </HStack>

                    <Checkbox
                      size="sm"
                      isChecked={includeBroaderSources}
                      onChange={(e) => setIncludeBroaderSources(e.target.checked)}
                      colorScheme="purple"
                    >
                      <Text fontSize="sm">Sources académiques élargies</Text>
                    </Checkbox>
                  </VStack>
                </CardBody>
              </Card>

              <Button
                colorScheme="purple"
                size="lg"
                leftIcon={<LuSearch />}
                onClick={() => handleSearch({ page: 1 })}
                isLoading={isSearching}
                borderRadius="xl"
              >
                Rechercher
              </Button>

              {/* Results */}
              {searchResults.length > 0 && (
                <VStack spacing={4} align="stretch" pt={4}>
                  <Text fontSize="sm" color="gray.600">
                    {rangeStart}-{rangeEnd} sur {totalResultsCount} résultats
                  </Text>
                  {searchResults.map((article, index) => (
                    <ArticleCard
                      key={createArticleKey(article, `${index}`) ?? `${index}`}
                      article={article}
                      highlightText={searchQuery.trim() ? highlightContent : undefined}
                      onBookmark={() => handleBookmark(article)}
                      onAddToProject={() => handleAddToProject(article)}
                      cardBg={cardBg}
                      borderColor={borderColor}
                      hoverBg={hoverBg}
                    />
                  ))}
                  {totalResultsCount > 0 && (
                    <PaginationCarousel
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      isLoading={isSearching}
                    />
                  )}
                </VStack>
              )}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Bookmarks Drawer */}
      <Drawer
        isOpen={isBookmarksDrawerOpen}
        placement="right"
        onClose={closeBookmarksDrawer}
        size="xl"
      >
        <DrawerOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader
            bgGradient="linear(135deg, purple.500 0%, purple.700 100%)"
            color="white"
          >
            <HStack>
              <Icon as={LuBookmark} />
              <Text>Mes favoris</Text>
            </HStack>
          </DrawerHeader>

          <DrawerBody px={6} pt={4}>
            {isLoadingBookmarks ? (
              <Stack spacing={4}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} height="200px" borderRadius="xl" />
                ))}
              </Stack>
            ) : bookmarkedArticles.length === 0 ? (
              <VStack spacing={4} py={12}>
                <Icon as={LuBookmark} boxSize={16} color="gray.300" />
                <Text color="gray.500">Aucun article sauvegardé</Text>
              </VStack>
            ) : (
              <VStack spacing={4} align="stretch">
                {bookmarkedArticles.map((article, index) => (
                  <ArticleCard
                    key={createArticleKey(article, `bookmark-${index}`) ?? `bookmark-${index}`}
                    article={article}
                    isBookmarked
                    onAddToProject={() => handleAddToProject(article)}
                    cardBg={cardBg}
                    borderColor={borderColor}
                    hoverBg={hoverBg}
                  />
                ))}
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Add to Project Modal */}
      <Modal isOpen={isProjectModalOpen} onClose={closeProjectModal} size="md">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="2xl">
          <ModalHeader>Ajouter à un projet</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {selectedArticleForProject && (
                <Box>
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    Article:
                  </Text>
                  <Text fontWeight="semibold" noOfLines={2}>
                    {selectedArticleForProject.title}
                  </Text>
                </Box>
              )}

              <FormControl isRequired>
                <FormLabel>Sélectionner un projet</FormLabel>
                {isLoadingProjects ? (
                  <Flex justify="center" py={4}>
                    <Spinner size="sm" colorScheme="purple" />
                  </Flex>
                ) : researchProjects.length === 0 ? (
                  <VStack spacing={3} py={4}>
                    <Text color="gray.500" fontSize="sm">
                      Aucun projet. Créez-en un d'abord.
                    </Text>
                    <Button
                      size="sm"
                      colorScheme="purple"
                      onClick={() => {
                        closeProjectModal()
                        openCreateProjectModal()
                      }}
                    >
                      Créer un projet
                    </Button>
                  </VStack>
                ) : (
                  <Select
                    placeholder="Sélectionner un projet"
                    value={selectedProjectId}
                    onChange={(e) => setSelectedProjectId(e.target.value)}
                    borderRadius="lg"
                  >
                    {researchProjects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.title}
                      </option>
                    ))}
                  </Select>
                )}
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeProjectModal} borderRadius="lg">
              Annuler
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleConfirmAddToProject}
              isLoading={isAddingToProject}
              isDisabled={!selectedProjectId || isLoadingProjects}
              borderRadius="lg"
            >
              Ajouter
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={isCreateProjectModalOpen}
        onClose={closeCreateProjectModal}
        onSubmit={async (projectData) => {
          try {
            await axiosClient.post('medinsight/projects', projectData)
            toast({
              title: 'Projet créé',
              status: 'success',
              duration: 3000,
              isClosable: true,
            })
            fetchProjects()
            closeCreateProjectModal()
          } catch (error) {
            toast({
              title: 'Erreur',
              description: 'Impossible de créer le projet',
              status: 'error',
              duration: 3000,
              isClosable: true,
            })
          }
        }}
      />
    </Box>
  )
}
